import type { ReactNode } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import type { Currency } from '../../types/Currency';
import type { PositionReconStatus, PositionWarning } from '../../types/Position';
import { ReconStreamStatusEnumLabels } from '../../types/ReconStatus';
import { WARNING_SEVERITY_TO_ALERT_VARIANT } from '../../types/WarningSeverity';
import { ReconStreamStatusEnum } from '../../types/types';
import { formattedDate } from '../../utils/date';
import { Alert } from '../Alert';
import { HStack, VStack } from '../Core';
import { InlineFormattedNumber } from '../FormattedNumber';
import { Text } from '../Text';

interface PositionWarningsProps {
  warning: PositionWarning;
  variant?: 'default' | 'mobileDetail';
}

export function PositionWarnings({ warning, variant = 'default' }: PositionWarningsProps) {
  const { severity, asset, reconStatus } = warning;

  const currency = useCurrency(asset);
  const isMobileDetail = variant === 'mobileDetail';

  const details = displayReconStatusDetails(reconStatus, currency);

  return (
    <VStack gap="spacing16" w="100%" p={isMobileDetail ? undefined : 'spacing8'}>
      <Alert
        w="100%"
        variant={WARNING_SEVERITY_TO_ALERT_VARIANT[severity]}
        dismissable={false}
        justifyContent={isMobileDetail ? undefined : 'space-between'}
        alignItems={isMobileDetail ? undefined : 'center'}
        fontSize={isMobileDetail ? 'fontSizeMd' : undefined}
      >
        {isMobileDetail ? (
          <VStack alignItems="flex-start" gap="spacing8" w="100%" flex={1} minWidth={0}>
            <Text size="fontSizeMd" color="colorTextDefault" textAlign="left">
              {ReconStreamStatusEnumLabels[reconStatus.Status]}
            </Text>
            {details}
          </VStack>
        ) : (
          <Text size="fontSizeMd" color="gray.100">
            {ReconStreamStatusEnumLabels[reconStatus.Status]}
          </Text>
        )}
      </Alert>
      {!isMobileDetail && details}
    </VStack>
  );
}

function displayReconStatusDetails(reconStatus: PositionReconStatus, currency: Currency | undefined): ReactNode {
  switch (reconStatus.Status) {
    case ReconStreamStatusEnum.MatchingSinceLatestMismatch:
    case ReconStreamStatusEnum.LedgerAheadSinceLatestMismatch:
      return (
        <VStack gap="spacing4" alignItems="flex-start" w="100%" fontSize="fontSizeDefault">
          {reconStatus.TotalMismatch != null && (
            <HStack gap="spacing24" justifyContent="space-between" w="100%">
              <Text color="gray.100" flex={1}>
                Total mismatch
              </Text>
              <InlineFormattedNumber
                number={reconStatus.TotalMismatch}
                increment={currency?.DefaultIncrement}
                currency={currency?.Symbol}
                align="right"
              />
            </HStack>
          )}
          {reconStatus.LastMismatchTime != null && (
            <HStack gap="spacing24" justifyContent="space-between" w="100%">
              <Text color="gray.100" flex={1}>
                Last mismatch
              </Text>
              <Text textAlign="right">{formattedDate(reconStatus.LastMismatchTime)}</Text>
            </HStack>
          )}
        </VStack>
      );

    default:
      return <></>;
  }
}
